import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { EVENT_ADDON_ITEMS_META } from '../../../constants';
import { formatPriceForDisplay } from '../../../../../../../../util/format-money';
import InfoCircleIcon from '../../../../../../../../components/icons/InfoCircleIcon';
import Button from '../../../../../../../../components/global/Button';
import useOpenModal from '../../../../../../../../hooks/useOpenModal';
import { EVENT_ADDON_ITEM_INFORMATION } from '../../../../../../components/FreemiumEventPurchase/components/EventAddonItemInformationModal';
import { EVENT_ADDON_ITEM_TYPE } from '../../../../../../../../constants/event-items';
import { ALBUM_SUBTYPE } from '../../../../../../../../constants/albums';
import CustomControl from '../../../../../../../../components/global/CustomControl';
import Badge from '../../../../../../../../components/global/Badge';
import { getEventAddonItemTypeMetaKey } from '../../../util';

const propTypes = {
  eventAddonItem: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }).isRequired,
  isSelected: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
  eventType: PropTypes.oneOf(Object.values(ALBUM_SUBTYPE)),
  itemStyle: PropTypes.oneOf(['full', 'squared', 'bundle']),
  standaloneEventAddonType: PropTypes.oneOf(
    Object.values(EVENT_ADDON_ITEM_TYPE),
  ),
};

const EventAddonItemBlock = ({
  eventAddonItem,
  isSelected,
  onToggle,
  eventType,
  itemStyle = 'full',
  standaloneEventAddonType,
}) => {
  const eventAddonItemMetaKey = useMemo(() => {
    return getEventAddonItemTypeMetaKey(eventAddonItem.types, eventType);
  }, [eventAddonItem]);

  const {
    title,
    standaloneTitle,
    description,
    howItWorks,
    tileCover,
    priceLabel,
    badgeLabel,
  } = EVENT_ADDON_ITEMS_META[eventAddonItemMetaKey] ?? {};

  const priceLabelToRender = useMemo(() => {
    if (eventAddonItemMetaKey === 'PHOTO_ALBUMS') {
      return `FREE or $${formatPriceForDisplay(eventAddonItem?.price?.value)}`;
    }

    if (priceLabel) {
      return priceLabel;
    }

    if (eventAddonItem?.price?.value) {
      return <>${formatPriceForDisplay(eventAddonItem?.price?.value)}</>;
    }

    return 'Free';
  }, [eventAddonItem]);

  const openModal = useOpenModal();

  const classes = classNames({
    'freemium-event-create-addon-item': true,
    [`freemium-event-create-addon-item-${itemStyle}`]: !!itemStyle,
    selected: isSelected,
  });

  const titleToRender = standaloneEventAddonType
    ? (standaloneTitle?.(eventType) ?? title)
    : title;

  return (
    <Button btnStyle="reset" className={classes} onClick={onToggle}>
      <img
        src={tileCover}
        className="freemium-event-create-addon-item-cover"
        alt={description}
      />
      <div className="freemium-event-create-addon-item-content">
        <p>{titleToRender}</p>
        <Badge badgeOutline badgeStyle="white">
          {priceLabelToRender}
        </Badge>
      </div>
      <div className="freemium-event-create-addon-item-top-left-content">
        {badgeLabel && (
          <Badge
            badgeSize="sm"
            badgeStyle="dark-transparent"
            className="text-extra-bold"
          >
            {badgeLabel}
          </Badge>
        )}
      </div>
      <div className="freemium-event-create-addon-item-top-right-content">
        {howItWorks && (
          <Button
            btnStyle="reset"
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();

              openModal(EVENT_ADDON_ITEM_INFORMATION, {
                title: titleToRender,
                ...howItWorks,
              });
            }}
            className="mr-1 d-flex"
          >
            <InfoCircleIcon size={24} color="#ffffff" />
          </Button>
        )}
        <CustomControl
          type="checkbox"
          id={eventAddonItem.id}
          name={titleToRender}
          label={titleToRender}
          labelSrOnly
          checked={isSelected}
          readOnly
          size="lg"
          shape="squared"
          className="freemium-event-create-addon-item-checkbox m-0"
        />
      </div>
    </Button>
  );
};

EventAddonItemBlock.propTypes = propTypes;

export default EventAddonItemBlock;
